import {
  CSSProperties,
  HTMLAttributes,
  ReactNode,
  useEffect,
  useRef
} from 'react';

interface AdaptiveModalProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  visible: boolean;
  onDismiss?: () => void;
  children?: ReactNode;
  header?: ReactNode;
  footer?: ReactNode;
  closeAriaLabel?: string;
  disableContentPaddings?: boolean;
  size?: 'small' | 'medium' | 'large' | 'max';
}

export const AdaptiveModal = ({
  visible,
  onDismiss,
  children,
  header,
  footer,
  closeAriaLabel = 'Close modal',
  disableContentPaddings = false,
  className = '',
  style,
  size: _size,
  ...rest
}: AdaptiveModalProps) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const dialogStyle: CSSProperties = {
    width: 'max-content',
    height: 'auto',
    maxWidth: 'calc(100vw - 2rem)',
    maxHeight: 'calc(100vh - 2rem)',
    ...style
  };

  useEffect(() => {
    if (!visible) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialogRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onDismiss?.();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [visible, onDismiss]);

  if (!visible) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/45 p-4"
      onClick={() => onDismiss?.()}
    >
      <div
        {...rest}
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        style={dialogStyle}
        className={`relative inline-flex flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ${className}`}
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={() => onDismiss?.()}
          aria-label={closeAriaLabel}
          className="absolute right-3 top-3 z-10 rounded-md p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
        >
          ×
        </button>
        {header ? (
          <div className="border-b border-slate-200 px-6 py-4 pr-12">
            {typeof header === 'string' ? (
              <h2 className="text-lg font-semibold text-slate-900">{header}</h2>
            ) : (
              header
            )}
          </div>
        ) : null}
        <div
          className={`min-h-0 overflow-auto ${
            disableContentPaddings ? '' : 'p-6'
          }`}
        >
          {children}
        </div>
        {footer ? (
          <div className="border-t border-slate-200 px-6 py-4">{footer}</div>
        ) : null}
      </div>
    </div>
  );
};
